import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, Stack, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { theme } from '../../../../../theme'
import CreateTwoToneIcon from '@mui/icons-material/CreateTwoTone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOfferLetterDialog from '../../../Admin/PreboardingConfiguration/EditOfferLetterDialog';
import ReactQuill from 'react-quill';
import { useGlobalContext } from '../../../../../Context/GlobalContext';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { acceptCandidateOfferLetter, declineCandidateOfferLetter, deleteCandidateOfferLetterFile, downloadCandidateOfferLetterPdf, getCandidateOfferLetter, updateCandidateOfferLetter, uploadCandidateOfferLetterFile } from '../candidatePreboardingApi';
import { removeSpacing, sanitizeContent } from '../../../../Common/Sanitize/sanitizeReactQuill';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { useDeleteDialog } from '../../../../../Context/DeleteConfirmationContext';
import DeclineOffer from './DeclineOffer';
import { useCandidatePreboarding } from '../CandidatePreboardingContext';
import { formatEpochToDate } from '../../../../Common/Date/ConvertDate';
import PdfPreview from './PdfPreview';
import { useLoadingBackdrop } from '../../../../../Context/LoadingBackdropContext';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { statusStyles } from '../../AllCandidates';


interface OfferLetterData {
  offerLetterId: number;
  offerLetterBody: string;
  mapId: number;
  hasOfferLetterFile: boolean;
  offerLetterSize: number;
  offerLetterFile: string | null;
  fileName: string | null;
}


const CandidateOffer = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const stateMapId = location.state?.mapID || 0;
  const { jobNum, candidateName, candidateNum } = location.state || {};
  const { tenant } = useParams<{ tenant: string }>();
  const [editedOfferLetterBody, setEditedOfferLetterBody] = useState('');
  const [isEdit, setIsEdit] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const quillRef = useRef<ReactQuill>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { userData } = useGlobalContext();
  const { showSnackbar } = useSnackbar();
  const { openDeleteDialog } = useDeleteDialog();
  const [isReviewed, setIsReviewed] = useState(false);
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const isCandidate = !!sessionStorage.getItem("candidate_session");
  const { candidateData, fetchCandidateData } = useCandidatePreboarding();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { show, hide } = useLoadingBackdrop();
  const [isOfferReviewedDialog, setIsOfferReviewedDialog] = useState(false)
  const [offerLetterData, setOfferLetterData] = useState<OfferLetterData>({
    offerLetterId: 0,
    offerLetterBody: '',
    mapId: stateMapId,
    hasOfferLetterFile: false,
    offerLetterSize: 0,
    offerLetterFile: null,
    fileName: null,
  });


  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
      [{ indent: '-1' }, { indent: '+1' }],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const fetchCandidateOfferLetter = async () => {
    try {
      setLoading(true)
      const payLoad = {
        MapID: offerLetterData.mapId,
        CandidateName: candidateName,
        JobNum: jobNum,
        CandidateNum: candidateNum
      }
      const result = await getCandidateOfferLetter(tenant, payLoad, isCandidate);
      if (result) {

        setOfferLetterData({
          offerLetterId: result.OfferLetterId,
          offerLetterBody: result.OfferLetter,
          mapId: result.MapID,
          hasOfferLetterFile: result.HasOfferLetterFile,
          offerLetterSize: result.OfferLetterSize,
          offerLetterFile: result.OfferLetterFile || null,
          fileName: result.FileName || null,
        });

        if (result.OfferLetterFile) {
          const file = base64ToFile(
            result.OfferLetterFile,
            result.FileName || `offer_letter_${result.MapID}.pdf`
          );
          setSelectedFile(file);
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }

  }

  const base64ToFile = (base64String: string, fileName: string, mimeType = "application/pdf"): File => {
    const byteString = atob(base64String);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }
    return new File([byteArray], fileName, { type: mimeType });
  }


  useEffect(() => {
    if (offerLetterData.mapId && candidateName && jobNum) {
      fetchCandidateOfferLetter()
    }
  }, [tenant, offerLetterData.mapId, candidateName, jobNum]);

  const handleSave = async () => {
    const sanitizedValue = sanitizeContent(editedOfferLetterBody);
    if (!sanitizedValue) {
      setErrorMessage('Offer letter content cannot be empty.');
      return;
    }
    const body = removeSpacing(editedOfferLetterBody);
    const payLoad = {
      OfferLetter: body,
      MapID: offerLetterData.mapId,
      OfferLetterId: offerLetterData.offerLetterId,
    };
    try {
      const response = await updateCandidateOfferLetter(tenant, payLoad);
      if (response) {
        showSnackbar(response.Message, response.Status ? 'success' : 'error');
        if (response.Status) {
          fetchCandidateOfferLetter();
        }

      }
    } catch (error) {
      showSnackbar(
        'Failed to update the offer letter. Please try again.',
        'error'
      );
      console.error(error);
    } finally {
      setIsEdit(false);
    }
  };


  const handleFile = (file: File) => {
    if (file) {
      if (file.type !== "application/pdf") {
        showSnackbar("Only PDF files are allowed", "error");
        return;
      }
      if (file.size > offerLetterData.offerLetterSize * 1024 * 1024) { // 2MB limit
        showSnackbar(`File size exceeds ${offerLetterData.offerLetterSize}MB`, "error");
        return;
      }
      setSelectedFile(file);
      // TODO: upload API call here
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showSnackbar("Please select a file to upload", "error");
      return;
    }
    const formData = new FormData()
    formData.append('OfferLetterFile', selectedFile, selectedFile.name);
    formData.append('MapID', offerLetterData.mapId.toString());
    try {
      const response = await uploadCandidateOfferLetterFile(tenant, formData)
      if (response) {
        showSnackbar(response.Message, response.Status ? 'success' : 'error');
        if (response.Status) {
          fetchCandidateOfferLetter();
          setSelectedFile(null); // Clear selected file after upload
        }
      }
    } catch (error) {
      console.log(error)
      showSnackbar('Failed to upload the offer letter file. Please try again.', 'error');
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDeleteFile = async () => {
    try {
      const payLoad = {
        MapID: offerLetterData.mapId
      }
      const response = await deleteCandidateOfferLetterFile(tenant, payLoad);

      if (response) {
        showSnackbar(response.Message, response.Status ? 'success' : 'error');
        if (response.Status) {
          setSelectedFile(null); // Clear selected file after deletion
          if (fileInputRef.current) fileInputRef.current.value = "";
          fetchCandidateOfferLetter();
        }
      }
    } catch (error) {
      console.log(error);
      showSnackbar('Failed to delete the offer letter file. Please try again.', 'error');
    }
  }

  // usage
  const handleDownloadOfferLetter = async () => {
    show()
    try {
      const payLoad = {
        MapID: offerLetterData.mapId,
        CandidateName: candidateName,
        JobNum: jobNum,
        CandidateNum: candidateNum,
        HasOfferLetterFile: offerLetterData.hasOfferLetterFile,
      };

      const response = await downloadCandidateOfferLetterPdf(tenant, payLoad, isCandidate);

      if (!response) {
        showSnackbar("No response received while downloading resume.", "error");
        return;
      }

      if (response.data.type === 'application/json') {
        const text = await response.data.text(); // error blob as text
        const errorData = JSON.parse(text);
        showSnackbar(errorData.Message || "Failed to download offer letter", "error");
        return;
      }

      const blob = response.data;



      // Try to extract filename from headers
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = 'offerletter.pdf'; // fallback

      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);


    } catch (error) {
      console.log(error);
      showSnackbar("Failed to download the offer letter. Please try again.", "error");
    } finally {
      hide()
    }
  };

  const handleDeclineOffer = async (reason: string) => {
    show()
    try {
      const payLoad = {
        MapID: offerLetterData.mapId,
        Reason: reason,
        CandidateNum: candidateData?.candidateNum,
        JobNum: candidateData?.jobNum,
        RecruiterName: candidateData?.recruiterName,
        RecruiterEmail: candidateData?.recruiterEmail,
      }
      const response = await declineCandidateOfferLetter(tenant, payLoad, isCandidate)
      if (response) {
        if (response.Status) {
          setIsDeclineOpen(false);
          sessionStorage.removeItem('candidate_session');
          navigate(`/${tenant}/Status`, {
            state: { type: 'declined' }
          });
        } else {
          showSnackbar(response.Message, 'error');
          return;
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      hide()
    }
  }

  const handleAcceptOffer = async () => {
    if (!isReviewed) {
      setIsOfferReviewedDialog(true)
      return
    }
    show()
    try {
      const payLoad = {
        MapID: offerLetterData.mapId,
        CandidateNum: candidateData?.candidateNum,
        JobNum: candidateData?.jobNum,
        RecruiterName: candidateData?.recruiterName,
        RecruiterEmail: candidateData?.recruiterEmail,
        OfferStatus: true,
        OfferReviewed: isReviewed,
      }
      const response = await acceptCandidateOfferLetter(tenant, payLoad, isCandidate);
      if (response) {
        if (response.Status) {
          await fetchCandidateData();
          navigate(`/${tenant}/preboarding/all-candidates/candidate-preboarding/candidate-details`, {
            state: location.state
          });
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      hide()
    }
  }


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh', width: '100%' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {
        (userData?.UserRole && !offerLetterData.hasOfferLetterFile && !selectedFile && ["OfferPending", "OfferSentAwaitingAcceptance"].includes(candidateData?.preboardingStatusKey || "")) && (
          <Box sx={{ position: 'absolute', right: '0vw', mt: '1.4vw', top: '-4vw' }}>
            <Button
              variant="outlined"
              startIcon={<CreateTwoToneIcon sx={{ fontSize: '0.8vw' }} />}
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
              }}
              onClick={() => {
                setEditedOfferLetterBody(offerLetterData.offerLetterBody);
                setIsEdit(true);
              }}
            >
              Edit
            </Button>

          </Box>
        )
      }

      {
        (userData?.UserRole && offerLetterData.hasOfferLetterFile && ["OfferPending", "OfferSentAwaitingAcceptance"].includes(candidateData?.preboardingStatusKey || "")) && (
          <Box sx={{ position: 'absolute', right: '0vw', mt: '1.4vw', top: '-4vw' }}>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon sx={{ fontSize: '0.8vw' }} />}
              sx={{
                color: theme.palette.error.main,
                borderColor: theme.palette.error.main,
              }}
              onClick={() => {
                openDeleteDialog(handleDeleteFile, "Are you sure you want to delete this offer letter file?");
              }}
            >
              Delete File
            </Button>
          </Box>
        )
      }





      <Typography variant='h6' mt='1vw' sx={{ fontWeight: 'bold' }}>
        Offer Letter
      </Typography>

      {
        candidateData?.isDeclined && (
          <Stack sx={{
            border: '1px solid red',
            borderRadius: '0.5vw',
            p: '1vw',
            mt: '1vw',
            bgcolor: '#FFFBFA'
          }}>
            <Stack direction={"row"} spacing={1} alignItems="center" mt={1}>
              <Box
                sx={{
                  display: "inline-block",
                  px: 1.2,
                  py: 0.3,
                  borderRadius: "1vw",
                  fontSize: "0.8vw",
                  fontWeight: 500,
                  width: '7vw',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  cursor: 'default',

                  ...statusStyles["OfferDeclined"]
                }}>
                {statusStyles["OfferDeclined"].label}
              </Box>
              <Typography variant='subtitle2' color={"#344054"}> - {formatEpochToDate(candidateData.declinedDate)}</Typography>
            </Stack>
            <Typography variant='subtitle2' color="#344054" mt={1}>
              <Box component="span" fontWeight="bold" color={"#101828"}>Reason:</Box> {candidateData.DeclineReason}
            </Typography>
          </Stack>

        )
      }



      <Box
        sx={{
          display: "flex",
          gap: "2vw",
        }}
      >
        {/* Left side: Preview */}
        {selectedFile ? (
          <PdfPreview file={selectedFile} />
        ) : (
          <Box
            mt="1vw"
            dangerouslySetInnerHTML={{ __html: offerLetterData.offerLetterBody }}
            sx={{ width: userData?.UserRole ? "60vw" : "87vw" }}
          />
        )}

        {/* Right side: Upload Section */}
        {(userData?.UserRole && !offerLetterData.hasOfferLetterFile && ["OfferPending", "OfferSentAwaitingAcceptance"].includes(candidateData?.preboardingStatusKey || "")) && (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {/* Drag & Drop Box */}
            <Box
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: "2px dashed",
                borderColor: dragActive
                  ? theme.palette.primary.dark
                  : theme.palette.primary.main,
                borderRadius: "0.5vw",
                padding: "2vw",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "20vh",
                width: "25vw",
                position: "relative",
                cursor: "pointer",
                bgcolor: dragActive ? theme.palette.action.hover : "transparent",
                transition: "0.2s",
              }}
            >
              {/* Hidden file input */}
              <input
                type="file"
                accept="application/pdf"
                ref={fileInputRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "pointer",
                  zIndex: 2,
                  pointerEvents: selectedFile ? "none" : "auto", // Prevent click when file is selected
                }}
                onChange={(e) => {
                  if (e.target.files?.[0]) handleFile(e.target.files[0]);
                }}
              />
              {selectedFile ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  px={2}
                  zIndex={3}
                  sx={{ position: "relative", pointerEvents: "auto" }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ maxWidth: "80%", color: theme.palette.text.primary }}
                  >
                    {selectedFile.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    sx={{ zIndex: 4 }}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon
                    sx={{
                      fontSize: "4vh",
                      color: theme.palette.primary.main,
                      zIndex: 1,
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ zIndex: 1 }}
                  >
                    {dragActive ? "Drop the file here" : "Click to upload"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ zIndex: 1 }}>
                    or drag and drop
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ zIndex: 1 }}>
                    (Upload only <strong>PDF</strong> file)
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ zIndex: 1 }}>
                    (Maximum size: <strong>{offerLetterData.offerLetterSize}MB</strong>)
                  </Typography>
                </>
              )}
            </Box>

            {/* ✅ Upload Button */}
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedFile}
              onClick={handleUpload} // call your upload API
              sx={{ width: "60%" }} // same width as upload box
            >
              Upload
            </Button>
          </Box>
        )}
      </Box>



      {
        (!userData?.UserRole && !candidateData?.isOfferAccepted && !candidateData?.isDeclined) && (
          <Box
            sx={{
              mt: '1vw'
            }}>
            <Checkbox
              checked={isReviewed}
              onChange={(e) => setIsReviewed(e.target.checked)}
            />
            <Typography variant='subtitle3' color={theme.palette.text.primary} fontWeight={600}>I acknowledge that I have read and reviewed the offer letter in full and that I understand and accept its terms and conditions.</Typography>

            <Divider sx={{ my: 2, borderBottomWidth: 2 }} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  color: theme.palette.primary.main
                }}
                onClick={handleDownloadOfferLetter}
              >
                Download Offer Letter
              </Button>
              <Stack direction="row" spacing={2} >
                <Button
                  variant='outlined'
                  // disabled={!isReviewed}
                  onClick={() => setIsDeclineOpen(true)}
                >
                  Decline
                </Button>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                  }}
                  // disabled={!isReviewed}
                  onClick={handleAcceptOffer}
                >
                  Accept Offer
                </Button>
              </Stack>
            </Box>

          </Box>
        )
      }

      {
        (isCandidate && candidateData?.isOfferAccepted && candidateData?.actionDate) && (
          <>
            <Divider sx={{ my: 2, borderBottomWidth: 2 }} />
            <Box sx={{ mt: '1vw', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

              <Button
                variant="outlined"
                sx={{
                  color: theme.palette.primary.main
                }}
                onClick={handleDownloadOfferLetter}
              >
                Download Offer Letter
              </Button>
              <Typography variant='subtitle1'>
                You have already accepted the offer letter on: {formatEpochToDate(candidateData.actionDate)}
              </Typography>
            </Box>
          </>

        )
      }

      {
        (isCandidate && candidateData?.isDeclined && candidateData?.declinedDate) && (
          <>
            <Divider sx={{ my: 2, borderBottomWidth: 2 }} />
            <Box sx={{ mt: '1vw', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

              <Button
                variant="outlined"
                sx={{
                  color: theme.palette.primary.main
                }}
                onClick={handleDownloadOfferLetter}
              >
                Download Offer Letter
              </Button>
              <Typography variant='subtitle1'>
                You have already declined the offer letter on: {formatEpochToDate(candidateData.declinedDate)}
              </Typography>
            </Box>
          </>

        )
      }


      <EditOfferLetterDialog
        open={isEdit}
        onCancel={() => setIsEdit(false)}
        onSave={handleSave}
        quillRef={quillRef}
        editedEmailBody={editedOfferLetterBody}
        setEditedEmailBody={setEditedOfferLetterBody}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        quillModules={quillModules}
      />

      <DeclineOffer
        open={isDeclineOpen}
        onClose={handleDeclineOffer}
        setDeclinePopupOpen={setIsDeclineOpen}

      />


      <Dialog
        open={isOfferReviewedDialog}
        onClose={() => { }}
        maxWidth="xs"
        fullWidth
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 999,
          backgroundColor: "rgba(0,0,0,0.3)", // lighter blackout
        }}
        transitionDuration={200}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorOutlineIcon color="warning" fontSize="large" />
            <Typography variant="h6">Action Required</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color={'#475467'}>
            Please acknowledge terms and conditions before accepting offer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsOfferReviewedDialog(false) }} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CandidateOffer